import { Color } from "@tiptap/extension-color";
import FileHandler from "@tiptap/extension-file-handler";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import {
  BubbleMenu,
  Editor,
  EditorContent,
  EditorContentProps,
  Extensions,
  useEditor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Divider } from "antd-v5";
import DisabledContext from "antd-v5/es/config-provider/DisabledContext";
import classNames from "classnames";
import useUpdateEffect from "hooks/useUpdateEffect";
import { compact, noop } from "lodash";
import React, {
  forwardRef,
  memo,
  useContext,
  useEffect,
  useImperativeHandle,
} from "react";
import ImageExtension from "tiptap/ImageExtension";
import { validateFileForUpload } from "utils/fileUtils";
import { escapeCodeBlocksBeforeSanitize } from "utils/parserUtil";

import BoldIcon from "ac-components/ac-icons/BoldIcon";
import H1Icon from "ac-components/ac-icons/H1Icon";
import H2Icon from "ac-components/ac-icons/H2Icon";
import H3Icon from "ac-components/ac-icons/H3Icon";
import ItalicIcon from "ac-components/ac-icons/ItalicIcon";
import LinkIcon from "ac-components/ac-icons/LinkIcon";
import OrderedListIcon from "ac-components/ac-icons/OrderedListIcon";
import UnorderedListIcon from "ac-components/ac-icons/UnorderedListIcon";

import "./ACEditor.less";

export type ACEditorProps = {
  slotBeforeRender?: (editor: Editor) => React.ReactNode;
  slotAfterRender?: (editor: Editor) => React.ReactNode;
  value?: string;
  onChange?: (content: string) => void;
  placeholder?: string;
  disabled?: boolean;
  bubbleMenu?: boolean;
  onFocus?: EditorContentProps["onFocus"];
  onBlur?: EditorContentProps["onBlur"];
  extensions?: Extensions;
  className?: string;
  id?: string;
  allowAttachments?: boolean;
  allowImages?: boolean;
};

export type ACEditorRef = {
  editor: Editor | null;
};

const handleFileDrop = (editorInstance: Editor, files: File[]) => {
  const file = validateFileForUpload(files);
  if (!file) return;
  const isImage = file.type.indexOf("image") > -1;
  const hasExtension = editorInstance.extensionManager.extensions.some(
    (ext) => ext.name === (isImage ? "image" : "file-attachment"),
  );
  if (!hasExtension) return;
  if (isImage) {
    editorInstance.chain().focus().insertImageAttachment(file).run();
  } else {
    editorInstance.chain().focus().insertFileAttachment(file).run();
  }
  editorInstance.commands.setTextSelection(
    editorInstance.state.selection.$from.pos + 2,
  );
};

const ACEditor = forwardRef<ACEditorRef, ACEditorProps>(
  (
    {
      slotBeforeRender,
      slotAfterRender,
      value,
      onChange = noop,
      placeholder = "Write something",
      disabled: customDisbale,
      bubbleMenu = true,
      onFocus,
      onBlur,
      extensions = [],
      className,
      id,
      allowAttachments = true,
      allowImages,
    },
    ref,
  ) => {
    const disabled = useContext(DisabledContext);
    const mergedDisbale = customDisbale || disabled;

    // Escape HTML inside code blocks to prevent TipTap from rendering them as actual HTML
    const processedValue = value
      ? escapeCodeBlocksBeforeSanitize(value)
      : value;

    const editor = useEditor({
      extensions: compact([
        Color.configure({ types: [TextStyle.name, ListItem.name] }),
        Link.extend({
          exitable: true,
          inclusive: false,
        }).configure({
          openOnClick: false,
          autolink: true,
        }),
        StarterKit.configure({
          bulletList: {
            keepMarks: true,
            keepAttributes: true,
          },
          orderedList: {
            keepMarks: true,
            keepAttributes: true,
          },
        }),
        (allowImages ?? allowAttachments) ? ImageExtension : undefined,
        allowImages || allowAttachments
          ? FileHandler.configure({
              onDrop: (currentEditor, files) => {
                if (!currentEditor.options.editable) return;
                handleFileDrop(currentEditor, files);
              },
            })
          : undefined,
        Placeholder.configure({
          placeholder,
        }),
        Underline,
        Table.configure({
          resizable: !mergedDisbale,
        }),
        TableRow,
        TableHeader,
        TableCell,
        ...extensions,
      ]),
      content: processedValue,
      onUpdate: ({ editor }) => onChange(editor.getHTML()),
    });

    useImperativeHandle(ref, () => ({ editor }), [editor]);

    useUpdateEffect(() => {
      if (editor && editor.getHTML() !== value) {
        // Escape HTML inside code blocks before setting content
        const processedContent = value
          ? escapeCodeBlocksBeforeSanitize(value)
          : "";
        editor.commands.setContent(processedContent);
      }
    }, [value, editor]);

    useEffect(() => {
      if (editor) {
        editor.setOptions({ editable: !mergedDisbale });
      }
    }, [editor, mergedDisbale]);

    return (
      <div
        className={classNames("ac-editor", className, {
          "ac-editor-disabled": mergedDisbale,
        })}
        id={id}
        onDragOver={
          allowImages || allowAttachments
            ? (e) => e.preventDefault()
            : undefined
        }
        onDrop={
          allowImages || allowAttachments
            ? (e) => {
                if (e.defaultPrevented) return;
                const files = e.dataTransfer?.files;
                if (!files?.length) return;
                e.preventDefault();
                if (!editor || mergedDisbale) return;
                handleFileDrop(editor, Array.from(files));
              }
            : undefined
        }
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            // prevent escape propagation to avoid modal close as escape has other functionalities inside the editor
            e.stopPropagation();
          }
        }}
      >
        {editor && slotBeforeRender ? slotBeforeRender(editor) : null}
        {bubbleMenu && <CustomBubbleMenu editor={editor} />}
        {/* <CustomFloatingMenu editor={editor} /> */}
        <div className="ac-editor-content-wrapper">
          <EditorContent editor={editor} onFocus={onFocus} onBlur={onBlur} />
        </div>
        {editor && slotAfterRender ? slotAfterRender(editor) : null}
      </div>
    );
  },
);

export default memo(ACEditor);

const CustomBubbleMenu = ({ editor }: { editor: Editor | null }) => {
  const setLink = () => {
    if (!editor) {
      return;
    }
    if (editor.isActive("link")) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      const url = window.prompt("URL");
      if (url) {
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: url })
          .run();
      }
    }
  };

  return (
    editor && (
      <BubbleMenu
        className="bubble-menu"
        tippyOptions={{ duration: 100 }}
        editor={editor}
      >
        <div className="bubble-menu-group">
          <HeadingIconButton level={1} editor={editor} />
          <HeadingIconButton level={2} editor={editor} />
          <HeadingIconButton level={3} editor={editor} />
        </div>
        <Divider type="vertical" className="m-x-0" />
        <OrderedListIcon
          className={editor.isActive("orderedList") ? "is-active" : ""}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <UnorderedListIcon
          className={editor.isActive("bulletList") ? "is-active" : ""}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <Divider type="vertical" className="m-x-0" />
        <div className="bubble-menu-group">
          <BoldIcon
            className={editor.isActive("bold") ? "is-active" : ""}
            onClick={() => editor.chain().focus().toggleBold().run()}
          />
          <ItalicIcon
            className={editor.isActive("italic") ? "is-active" : ""}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          />
        </div>
        <Divider type="vertical" className="m-x-0" />
        <div className="bubble-menu-group">
          <LinkIcon
            className={editor.isActive("link") ? "is-active" : ""}
            onClick={setLink}
          />
        </div>
      </BubbleMenu>
    )
  );
};

const HeadingIconButton = ({
  level,
  editor,
}: {
  level: 1 | 2 | 3;
  editor: Editor;
}) => {
  const onClick = () => {
    editor.chain().focus().toggleHeading({ level }).run();
  };
  const className = editor.isActive("heading", { level }) ? "is-active" : "";
  const Component = [H1Icon, H2Icon, H3Icon][level - 1];
  return <Component onClick={onClick} className={className} />;
};

// const CustomFloatingMenu = ({ editor }: { editor: Editor | null }) => {
//   const addImage = () => {
//     const fileChanged = () => {
//       const file = validateFileForUpload(fileHolder.files!);
//       if (file) {
//         editor?.chain().focus().setImage().uploadImage(file).run();
//       }
//     };
//     const fileHolder = document.createElement("input");
//     fileHolder.setAttribute("type", "file");
//     fileHolder.setAttribute("accept", "*");
//     fileHolder.onchange = fileChanged;
//     fileHolder.click();
//   };
//   return (
//     editor && (
//       <FloatingMenu
//         className="floating-menu"
//         tippyOptions={{ duration: 100 }}
//         editor={editor}
//       >
//         <HeadingIconButton level={1} editor={editor} />
//         <HeadingIconButton level={2} editor={editor} />
//         <HeadingIconButton level={3} editor={editor} />
//         <button
//           onClick={() => editor.chain().focus().toggleBulletList().run()}
//           className={editor.isActive("bulletList") ? "is-active" : ""}
//         >
//           Bullet list
//         </button>
//         <ImageIcon onClick={addImage} />
//       </FloatingMenu>
//     )
//   );
// };
